import React, { useState, useEffect, useCallback, useRef } from 'react';
import PurchaseChallanTable from '../components/purchase-challan/PurchaseChallanTable';
import PurchaseChallanActions from '../components/purchase-challan/PurchaseChallanActions';
import PurchaseChallanViewModal from '../components/purchase-challan/PurchaseChallanViewModal';
import PurchaseChallanEditModal from '../components/purchase-challan/PurchaseChallanEditModal';
import PurchaseChallanPrintModal from '../components/purchase-challan/PurchaseChallanPrintModal';
import usePurchaseOrder from '../hooks/usePurchaseOrder';
import { useMessageAlert } from '../components/common/MessageAlert';
import MessageAlert from '../components/common/MessageAlert';
import Pagination from '../components/common/Pagination';
import ExportModal from '../components/common/ExportModal';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import { useTranslation } from 'react-i18next';

const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'purchase_challan_page_size';

const PurchaseChallanPage = () => {
    const { t } = useTranslation();
    const {
        purchaseOrders: challans,
        loading,
        fetchPurchaseChallans,
        getPurchaseChallanById,
        editPurchaseChallan,
        exportPurchaseChallans,
        markAdviceChallanPrinted,
        totalPages,
        totalCount,
    } = usePurchaseOrder();

    const { showSuccess, showError } = useMessageAlert();
    const { userRole } = useRole();

    // State for View Modal
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedChallan, setSelectedChallan] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);

    // State for Edit Modal
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editChallan, setEditChallan] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    // State for Print Modal
    const [printModalOpen, setPrintModalOpen] = useState(false);
    const [printChallan, setPrintChallan] = useState(null);
    const [printLoading, setPrintLoading] = useState(false);

    // State for Export Modal
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    // Pagination & Filters
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [committedSearch, setCommittedSearch] = useState('');

    const today = new Date().toLocaleDateString('en-CA');
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const searchDebounceRef = useRef(null);

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setCommittedSearch(value);
            setCurrentPage(1);
        }, 400);
    };

    const handleFromDateChange = (value) => {
        setFromDate(value);
        setCurrentPage(1);
    };

    const handleToDateChange = (value) => {
        setToDate(value);
        setCurrentPage(1);
    };

    const loadChallans = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshLoading(true);
        try {
            const skip = (currentPage - 1) * pageSize;
            await fetchPurchaseChallans(
                {
                    skip,
                    limit: pageSize,
                    challan_printed: false,
                    ...(committedSearch ? { search: committedSearch } : {}),
                    ...(fromDate ? { from_date: fromDate } : {}),
                    ...(toDate ? { to_date: toDate } : {}),
                },
                { force: isRefresh }
            );
        } catch (err) {
            console.error('Failed to load challans', err);
        } finally {
            if (isRefresh) setRefreshLoading(false);
        }
    }, [fetchPurchaseChallans, currentPage, committedSearch, fromDate, toDate, pageSize]);

    useEffect(() => {
        loadChallans();
    }, [loadChallans]);

    const handleRefresh = () => {
        loadChallans(true);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
    };

    const handleExport = async ({ from_date, to_date, format }) => {
        setExportLoading(true);
        try {
            const result = await exportPurchaseChallans({ 
                from_date, 
                to_date, 
                format,
                ...(committedSearch && { search: committedSearch })
            });
            if (result.success) {
                setShowExportModal(false);
            } else {
                showError(result.error || t('purchaseChallan.errors.exportFailed'));
            }
        } finally {
            setExportLoading(false);
        }
    };

    // Handlers
    const handleViewChallan = async (challanNumber) => {
        setViewLoading(true);
        setViewModalOpen(true);
        try {
            const result = await getPurchaseChallanById(challanNumber);
            if (result.success) {
                setSelectedChallan(result.data);
            } else {
                showError(t('purchaseChallan.view.empty.message'));
                setViewModalOpen(false);
            }
        } catch (err) {
            console.error('Failed to load challan', err);
            showError(t('purchaseChallan.view.empty.message'));
            setViewModalOpen(false);
        } finally {
            setViewLoading(false);
        }
    };

    const handleEditChallan = async (challanNumber) => {
        setEditLoading(true);
        setEditModalOpen(true);
        try {
            const result = await getPurchaseChallanById(challanNumber);
            if (result.success) {
                setEditChallan(result.data);
            } else {
                showError(t('purchaseChallan.edit.errors.fetchFailed'));
                setEditModalOpen(false);
            }
        } catch (err) {
            console.error('Failed to load challan', err);
            showError(t('purchaseChallan.edit.errors.fetchFailed'));
            setEditModalOpen(false);
        } finally {
            setEditLoading(false);
        }
    };

    const handleUpdateChallan = async (payload) => {
        if (!editChallan) return;
        setEditLoading(true);
        try {
            const targetId = editChallan.advice_id || editChallan.id;
            const result = await editPurchaseChallan(targetId, payload);
            if (result.success) {
                showSuccess(t('purchaseChallan.edit.notifications.success'));
                setEditModalOpen(false);
                setEditChallan(null);
                loadChallans(true);
            } else {
                showError(result.error || t('purchaseChallan.edit.errors.updateFailed'));
            }
        } catch (err) {
            console.error('Failed to update challan', err);
            showError(t('purchaseChallan.edit.errors.updateFailed'));
        } finally {
            setEditLoading(false);
        }
    };

    const handlePrintChallan = async (challanNumber) => {
        setPrintLoading(true);
        setPrintModalOpen(true);
        try {
            const result = await getPurchaseChallanById(challanNumber);
            if (result.success) {
                setPrintChallan(result.data);
            } else {
                showError(t('purchaseChallan.view.empty.message'));
                setPrintModalOpen(false);
            }
        } catch (err) {
            console.error('Failed to load challan', err);
            showError(t('purchaseChallan.view.empty.message'));
            setPrintModalOpen(false);
        } finally {
            setPrintLoading(false);
        }
    };



    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
            {alertMessage && (
                <div className="fixed top-4 right-4 z-50">
                    <MessageAlert type={alertMessage.type} title={alertMessage.title} message={alertMessage.message} onClose={() => setAlertMessage(null)} />
                </div>
            )}

            <div className="mb-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{t('navigation.pages.purchaseChallan')}</h1>
                        <p className="mt-2 text-gray-600">{t('navigation.pageSubtitles.purchaseChallan')}</p>
                    </div>

                    <div className="flex space-x-4">
                        <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-3">
                                <div className="text-sm text-gray-500">{t('navigation.pageMetrics.totalChallans')}</div>
                                <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <PurchaseChallanActions
                showSearch={true}
                searchValue={searchQuery}
                onSearchChange={handleSearchChange}
                loading={loading}
                refreshLoading={refreshLoading}
                onRefresh={handleRefresh}
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={handleFromDateChange}
                onToDateChange={handleToDateChange}
                onExport={() => setShowExportModal(true)}
                exportLoading={exportLoading}
                canExport={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.ACCOUNT_USER, USER_ROLES.SALES_STAFF, USER_ROLES.OPERATIONS_MANAGER].includes(userRole)}
            />
            <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6 overflow-hidden">
                <PurchaseChallanTable
                    challans={challans || []}
                    loading={loading}
                    onViewChallan={handleViewChallan}
                    onEditChallan={handleEditChallan}
                    onPrintChallan={handlePrintChallan}
                    viewLoading={viewLoading}
                />
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={setCurrentPage}
                loading={loading}
                totalLabel="total challans"
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
            />

            {/* Modals */}
            {viewModalOpen && (
                <PurchaseChallanViewModal
                    isOpen={viewModalOpen}
                    onClose={() => setViewModalOpen(false)}
                    challan={selectedChallan}
                    loading={viewLoading}
                />
            )}

            {editModalOpen && (
                <PurchaseChallanEditModal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    onSubmit={handleUpdateChallan}
                    challan={editChallan}
                    loading={editLoading}
                />
            )}

            {printModalOpen && (
                <PurchaseChallanPrintModal
                    isOpen={printModalOpen}
                    onClose={() => setPrintModalOpen(false)}
                    data={printChallan}
                    loading={printLoading}
                    onMarkChallanPrinted={() => markAdviceChallanPrinted(printChallan?.advice_id || printChallan?.id)}
                />
            )}

            {showExportModal && (
                <ExportModal
                    isOpen={showExportModal}
                    onClose={() => setShowExportModal(false)}
                    onExport={handleExport}
                    loading={exportLoading}
                    title={t('common.actions.exportPurchaseChallans')}
                />
            )}
        </div>
    );
};

export default PurchaseChallanPage;
